import React, { useState } from 'react';
import { authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Send, Link2, Loader2, AlertTriangle, CheckCircle2, ChevronDown, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export const payLinkFor = (invoice) => invoice.invoice_url || `${window.location.origin}/invoice/${invoice.id}`;

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  }
};

const EmailHistory = ({ log }) => {
  const [open, setOpen] = useState(false);
  if (!log?.length) return null;
  return (
    <div className="text-xs">
      <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1 text-[#E8E4DE]/50 hover:text-white"
        data-testid="invoice-email-history-toggle">
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} /> Send history ({log.length})
      </button>
      {open && (
        <ul className="mt-2 space-y-1.5" data-testid="invoice-email-history">
          {[...log].reverse().map((e, i) => (
            <li key={i} className="flex gap-2">
              {e.success ? <CheckCircle2 className="h-3.5 w-3.5 text-green-400 shrink-0 mt-0.5" /> : <AlertTriangle className="h-3.5 w-3.5 text-red-400 shrink-0 mt-0.5" />}
              <span className="text-[#E8E4DE]/70">
                {new Date(e.at).toLocaleString()} → {e.to}
                {!e.success && e.error && <span className="block text-red-300/80">{e.error}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const InvoiceSendControls = ({ invoice, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [toEmail, setToEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);

  const checkStripe = async () => {
    setChecking(true);
    try {
      const res = await authFetch(`${API_BASE}/api/invoices/${invoice.id}/verify-payment`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not check Stripe');
      if (data.paid) {
        toast.success('Payment found in Stripe — invoice marked paid');
        const inv = await authFetch(`${API_BASE}/api/invoices/${invoice.id}`).then((r) => r.json());
        onUpdated?.(inv);
      } else {
        toast.info('No completed payment found in Stripe for this invoice yet', { description: data.message });
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setChecking(false);
    }
  };
  const unpaid = ['draft', 'sent'].includes(invoice.status);
  const failed = invoice.last_email_status === 'failed';

  const resend = async () => {
    setSending(true);
    try {
      const res = await authFetch(`${API_BASE}/api/invoices/${invoice.id}/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_email: toEmail.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(Array.isArray(data.detail) ? 'Please enter a valid email address' : data.detail || 'Resend failed');
      onUpdated?.(data);
      if (data.email_status?.success) {
        toast.success(`Invoice sent to ${data.email_status.recipient}`);
        setOpen(false);
        setToEmail('');
      } else {
        toast.error("Invoice email didn't send", { description: data.email_status?.error, duration: 10000 });
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  const copyPayLink = async () => {
    if (await copyText(payLinkFor(invoice))) toast.success('Pay link copied — paste it into a text message');
    else toast.error('Could not copy. Link: ' + payLinkFor(invoice));
  };

  return (
    <div className="space-y-3" data-testid="invoice-send-controls">
      {failed && (
        <div className="flex gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm" data-testid="invoice-email-error">
          <AlertTriangle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-red-300 font-medium">Last email didn't send</p>
            <p className="text-red-200/80">{invoice.last_email_error}</p>
          </div>
        </div>
      )}
      {invoice.last_email_status === 'sent' && (
        <p className="text-xs text-green-400/80 flex items-center gap-1" data-testid="invoice-email-ok">
          <CheckCircle2 className="h-3.5 w-3.5" /> Last emailed {new Date(invoice.email_log?.at(-1)?.at).toLocaleString()} to {invoice.email_log?.at(-1)?.to}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {unpaid && (
          <Button size="sm" onClick={() => setOpen(true)} className="bg-[#C6A14A] text-[#050816] hover:bg-[#d4b25c]" data-testid="resend-invoice-btn">
            <Send className="h-4 w-4 mr-1.5" /> Resend Invoice
          </Button>
        )}
        {unpaid && (
          <Button size="sm" variant="outline" onClick={checkStripe} disabled={checking}
            className="border-white/15 bg-transparent text-white hover:bg-white/5" data-testid="check-stripe-payment-btn">
            {checking ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Check Stripe Payment
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={copyPayLink}
          className="border-white/15 bg-transparent text-white hover:bg-white/5" data-testid="copy-pay-link-btn">
          <Link2 className="h-4 w-4 mr-1.5" /> Copy Pay Link
        </Button>
      </div>
      <EmailHistory log={invoice.email_log} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0f1724] border-white/10 text-white sm:max-w-md" data-testid="resend-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-light">Resend invoice {invoice.invoice_number}</DialogTitle>
            <DialogDescription className="text-[#E8E4DE]/60">
              Sends the same invoice and Pay link again. No new invoice is created.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-[#E8E4DE]/80">Send to a different email (optional)</Label>
            <Input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)}
              placeholder={invoice.customer_email || 'client@example.com'}
              className="bg-[#050816] border-white/10 text-white placeholder:text-white/30" data-testid="resend-invoice-email-input" />
            <p className="text-xs text-[#E8E4DE]/40">Leave blank to send to {invoice.customer_email || 'the client'}.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={sending}
              className="border-white/10 bg-transparent text-[#E8E4DE] hover:bg-white/5" data-testid="resend-invoice-cancel-btn">Cancel</Button>
            <Button onClick={resend} disabled={sending} className="bg-[#C6A14A] text-[#050816] hover:bg-[#d4b25c]" data-testid="resend-invoice-confirm-btn">
              {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Send Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
